import * as React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, IconButton, Box, useTheme, useMediaQuery,
} from "@mui/material";
import CloseIcon        from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export default function NewDialog({
  open,
  handleClose,
  handleSubmit,
  title,
  content,
  buttonText,
  cancelButtonText,
  isSubmitting    = false,
  isSubmittingText,
  textStatus      = false,
  text,
  isValidated     = false,
  enableActions   = true,
}) {
  const theme      = useTheme();
  const isDark     = theme.palette.mode === "dark";
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const bg      = theme.palette.background.alt;
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: fullScreen ? bg : cardBg,
          border: fullScreen ? "none" : `1px solid ${border}`,
          borderRadius: fullScreen ? 0 : "14px",
          boxShadow: isDark
            ? "0 24px 48px rgba(0,0,0,0.5)"
            : "0 24px 48px rgba(0,0,0,0.12)",
          overflow: "hidden",
          minWidth: { xs: "256px", sm: "700px" },
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.35)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "1.5rem",
          py: "1rem",
          borderBottom: `1px solid ${border}`,
          backgroundColor: isDark
            ? "rgba(29,158,117,0.05)"
            : "rgba(29,158,117,0.04)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={{
            width: "6px", height: "20px",
            borderRadius: "3px",
            backgroundColor: accent,
            flexShrink: 0,
          }} />
          <Typography sx={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: "17px", fontWeight: 400,
            color: textPri, lineHeight: 1.3,
          }}>
            {title}
          </Typography>
        </Box>

        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: textSec,
            borderRadius: "8px",
            border: `1px solid ${border}`,
            width: "30px", height: "30px",
            "&:hover": {
              backgroundColor: "rgba(226,75,74,0.08)",
              borderColor: "rgba(226,75,74,0.3)",
              color: "#e24b4a",
            },
            transition: "all 0.15s",
          }}
        >
          <CloseIcon sx={{ fontSize: "15px" }} />
        </IconButton>
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent
        sx={{
          px: "1.5rem",
          py: "1.25rem",
          color: textPri,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
            borderRadius: "4px",
          },
        }}
      >
        {/* Warning banner */}
        {textStatus && text && (
          <Box sx={{
            display: "flex", alignItems: "flex-start", gap: "10px",
            p: "0.875rem 1rem", mb: "1.25rem",
            backgroundColor: "rgba(186,117,23,0.07)",
            border: "1px solid rgba(186,117,23,0.25)",
            borderRadius: "8px",
          }}>
            <WarningAmberIcon sx={{ fontSize: "18px", color: "#BA7517", flexShrink: 0, mt: "1px" }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#FAC775" : "#854F0B", lineHeight: 1.6 }}>
              {text}
            </Typography>
          </Box>
        )}

        {/* Non-warning text */}
        {!textStatus && text && (
          <Typography sx={{ fontSize: "13px", color: textSec, mb: "1rem", lineHeight: 1.7 }}>
            {text}
          </Typography>
        )}

        {content}
      </DialogContent>

      {/* ── Footer ── 
          Key difference from FormDialog: buttons only appear when isValidated is true.
          This matches the original NewDialog behaviour where the submit button 
          is gated behind validation (e.g. payment amount must equal invoice total).
      ── */}
      {enableActions && isValidated && (
        <DialogActions
          sx={{
            px: "1.5rem",
            py: "0.875rem",
            borderTop: `1px solid ${border}`,
            backgroundColor: isDark
              ? "rgba(29,158,117,0.03)"
              : "rgba(29,158,117,0.02)",
            gap: "0.5rem",
            justifyContent: "flex-end",
          }}
        >
          {/* Cancel — hidden while submitting */}
          {!isSubmitting && (
            <Button
              onClick={handleClose}
              sx={{
                fontSize: "13px", fontWeight: 500,
                px: "1rem", py: "0.4rem",
                borderRadius: "8px", textTransform: "none",
                border: `1px solid ${border}`,
                color: textSec, backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "rgba(226,75,74,0.06)",
                  borderColor: "rgba(226,75,74,0.3)",
                  color: "#e24b4a",
                },
                transition: "all 0.15s",
              }}
            >
              {cancelButtonText ?? "Cancel"}
            </Button>
          )}

          {/* Submit / loading */}
          <Button
            onClick={!isSubmitting ? handleSubmit : undefined}
            disabled={isSubmitting}
            sx={{
              fontSize: "13px", fontWeight: 500,
              px: "1rem", py: "0.4rem",
              borderRadius: "8px", textTransform: "none",
              backgroundColor: isSubmitting
                ? (isDark ? "rgba(29,158,117,0.3)" : "rgba(29,158,117,0.15)")
                : accent,
              color: isSubmitting ? accent : "#fff",
              border: isSubmitting ? `1px solid ${border}` : "none",
              "&:hover": { backgroundColor: isSubmitting ? undefined : "#0F6E56" },
              "&:disabled": { opacity: 1 },
              transition: "all 0.15s",
              display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            {isSubmitting && (
              <Box sx={{
                width: "13px", height: "13px",
                borderRadius: "50%",
                border: `1.5px solid ${accent}`,
                borderTopColor: "transparent",
                animation: "spin 0.6s linear infinite",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                flexShrink: 0,
              }} />
            )}
            {isSubmitting ? (isSubmittingText ?? "Please wait…") : buttonText}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}